import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, MenuItem, Button, IconButton,
    Tooltip, CircularProgress, Autocomplete, Chip
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { autocompletePopperSlotProps } from "../../utils/autocompletePopperSlotProps";
import BirthdateValueEditor from "../shared/BirthdateValueEditor";
import { BIRTHDATE_FIELD, normalizeBirthdateConditionValue } from "../../utils/birthdateWire";

const COLOR = SECTION_COLORS.rules;

// ─── Serialization ────────────────────────────────────────────────────────────

function serializeNode(node) {
    if (node.type === "group") {
        const children = (node.conditions || []).map(serializeNode).filter(Boolean);
        if (children.length === 0) return null;

        const gType = node.groupType || "and";
        if (gType === "not") {
            if (children.length === 1) return { not: children[0] };
            return { not: { and: children } };
        }
        // Always keep the group wrapper so its operator (or/and) is preserved,
        // even when there is only one child.
        return { [gType]: children };
    } else {
        if (!node.field) return null;
        return {
            field: node.field,
            operator: node.operator || "eq",
            value: normalizeBirthdateConditionValue(node.field, node.value),
        };
    }
}

export function buildConditionsJson(combinator, rows) {
    if (!rows || rows.length === 0) return null;
    const children = rows.map(serializeNode).filter(Boolean);
    if (children.length === 0) return null;
    // Always wrap in the envelope { [combinator]: [...] }.
    // parseConditionsJson always peels this envelope off — no ambiguity.
    return { [combinator || "and"]: children };
}

function parseNode(node) {
    if (!node || typeof node !== "object") return null;

    if ("and" in node && Array.isArray(node.and)) {
        return {
            id: `g_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: "group",
            groupType: "and",
            conditions: node.and.map(parseNode).filter(Boolean)
        };
    }
    
    if ("or" in node && Array.isArray(node.or)) {
         return {
            id: `g_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: "group",
            groupType: "or",
            conditions: node.or.map(parseNode).filter(Boolean)
        };
    }

    if ("not" in node) {
        const innerNode = node.not;
        let conditions = [];
        if (innerNode && typeof innerNode === "object") {
            if ("and" in innerNode && Array.isArray(innerNode.and)) {
                conditions = innerNode.and.map(parseNode).filter(Boolean);
            } else if ("or" in innerNode && Array.isArray(innerNode.or)) {
                conditions = innerNode.or.map(parseNode).filter(Boolean);
            } else {
                const parsed = parseNode(innerNode);
                if (parsed) conditions = [parsed];
            }
        }
        return {
            id: `g_not_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: "group",
            groupType: "not",
            conditions
        };
    }

    if ("field" in node) {
        return {
            id: `c_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: "condition",
            field: node.field || "",
            operator: node.operator || node.op || "eq",
            value: normalizeBirthdateConditionValue(node.field, node.value ?? ""),
        };
    }

    return null;
}

// parseConditionsJson: the inverse of buildConditionsJson.
// The top-level { and/or: [...] } is ALWAYS the envelope — its children become root rows.
// Each child is either a condition node or a group node.
export function parseConditionsJson(conditions) {
    const defaultResult = { combinator: "and", rows: [] };
    if (!conditions || typeof conditions !== "object") return defaultResult;

    for (const comb of ["and", "or"]) {
        if (comb in conditions && Array.isArray(conditions[comb])) {
            return {
                combinator: comb,
                rows: conditions[comb].map(parseNode).filter(Boolean),
            };
        }
    }

    // Edge-case: bare { not: ... } or single { field: ... } at root
    if ("not" in conditions || "field" in conditions) {
        const node = parseNode(conditions);
        return { combinator: "and", rows: node ? [node] : [] };
    }

    return defaultResult;
}

// ─── ValueEditor ──────────────────────────────────────────────────────────────
function ValueEditor({ field, operator, value, onChange, brandName, fieldCatalog }) {
    const numericFields = ["customer.lifetime_points","customer.status_points","customer.birthdate_month","customer.birthdate_day","customer.created_at_month","customer.created_at_day","system.today_month","system.today_day","system.customer_created_days","system.customer_last_activity_days"];
    const dateFields = ["customer.last_activity_at","customer.created_at","system.now"];
    const numericPatterns = [/\.metrics\./, /_count/, /_amount/, /_total/, /_score/, /_days/, /_age/, /_points/, /_value/];
    const isStatus = new Set(["status","customer.status"]).has(field);
    const isLoyalty = new Set(["loyalty_status","customer.loyalty_status"]).has(field);
    const isReward = new Set(["rewards","customer.rewards"]).has(field);
    const isGender = new Set(["gender","customer.gender"]).has(field);
    const isEnumField = isStatus || isLoyalty || isReward || isGender;
    const numericOps = new Set(["gt", "gte", "lt", "lte", "between"]);
    const isNumeric = !isEnumField && (
        numericFields.includes(field)
        || numericPatterns.some((p) => p.test(field))
        || (numericOps.has(operator) && !(["customer.birthdate", "customer.last_activity_at", "customer.created_at", "system.now"].includes(field)))
    );
    const isBirthdate = field === BIRTHDATE_FIELD;
    const isDate = dateFields.includes(field) || isBirthdate;
    const isDateOnly = isBirthdate;
    const isArrayOp = ["in","between"].includes(operator);
    const hasDynamicDate = isDate || isDateOnly;
    const isSysObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v) && "$system" in v;
    const initArr = Array.isArray(value) ? value : ["", ""];

    // ── ALL hooks unconditionally at top (Rules of Hooks) ───────────────────
    const [rewardOpts, setRewardOpts] = useState([]);
    const [tierOpts, setTierOpts] = useState([]);
    const [useDynamic, setUseDynamic] = useState(() => hasDynamicDate && !isArrayOp && isSysObj(value));
    const [dynPair, setDynPair] = useState(() => hasDynamicDate && isArrayOp && (isSysObj(initArr[0]) || isSysObj(initArr[1])));

    const sx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const SYS_PRESETS = [
        { k: "today", l: "Aujourd'hui (today)" },
        { k: "now",   l: "Maintenant (now)" },
    ];
    const makeSysObj = (base = "today", add = 0, fmt) => { const o = { $system: base }; if (add !== 0) o.add_days = add; if (fmt) o.format = fmt; return o; };


    useEffect(() => {
        if (!isReward || !brandName) return;
        loyaltyFetch(`${LOYALTY_API_BASE}/rewards?brand=${encodeURIComponent(brandName)}`, { headers: { "X-Brand": brandName } })
            .then((r) => r.ok ? r.json() : []).then((d) => setRewardOpts(Array.isArray(d) ? d : [])).catch(() => {});
    }, [isReward, brandName]);

    useEffect(() => {
        if (!isLoyalty || !brandName) return;
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers?brand=${encodeURIComponent(brandName)}&active=true`, { headers: { "X-Brand": brandName } })
            .then((r) => r.ok ? r.json() : []).then((d) => setTierOpts(Array.isArray(d) ? d : [])).catch(() => {});
    }, [isLoyalty, brandName]);

    if (isBirthdate) {
        return (
            <BirthdateValueEditor
                value={value}
                onChange={onChange}
                operator={operator}
                fieldMeta={fieldCatalog?.fieldMeta?.[BIRTHDATE_FIELD]}
                valuePresets={fieldCatalog?.valuePresets?.birthdate}
                accentColor={COLOR.main}
            />
        );
    }

    if (operator === "exists") return <TextField size="small" fullWidth select label="Existe ?" value={value === true || value === "true" ? "true" : "false"} onChange={(e) => onChange(e.target.value === "true")} sx={sx}><MenuItem value="true">Oui</MenuItem><MenuItem value="false">Non</MenuItem></TextField>;
    if (isGender) return <TextField size="small" fullWidth select label="Genre" value={value ?? ""} onChange={(e) => onChange(e.target.value)} sx={sx}>{["M","F","OTHER","UNKNOWN"].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField>;
    if (isStatus) return <TextField size="small" fullWidth select label="Statut" value={value ?? ""} onChange={(e) => onChange(e.target.value)} sx={sx}><MenuItem value="ACTIVE">Active</MenuItem><MenuItem value="INACTIVE">Inactive</MenuItem></TextField>;
    if (isLoyalty) {
        const multi = operator === "in";
        const rawSel = multi
            ? (Array.isArray(value) ? value.map(String) : [value].filter(Boolean).map(String))
            : (value != null && value !== "" ? String(value) : "");
        const byKey = new Map(tierOpts.map((t) => [String(t.key), t.name || String(t.key)]));
        return (
            <TextField size="small" fullWidth select label="Statut fidélité"
                value={rawSel}
                onChange={(e) => { const v = e.target.value; onChange(multi ? (Array.isArray(v) ? v : [v].filter(Boolean)) : v); }}
                sx={sx}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                    multiple: multi,
                    displayEmpty: true,
                    renderValue: (s) => multi
                        ? (Array.isArray(s) && s.length ? s.map((k) => byKey.get(String(k)) || k).join(", ") : <em style={{color:"#94a3b8"}}>Sélectionner…</em>)
                        : (byKey.get(String(s)) || s || <em style={{color:"#94a3b8"}}>Sélectionner…</em>)
                }}>
                {tierOpts.length === 0 && <MenuItem disabled value=""><em>Chargement…</em></MenuItem>}
                {tierOpts.map((t) => <MenuItem key={t.id || t.key} value={String(t.key)}>{t.name || t.key}</MenuItem>)}
            </TextField>
        );
    }
    if (isReward) {
        const multi = operator === "in";
        const rawSel = multi
            ? (Array.isArray(value) ? value.map(String) : [value].filter(Boolean).map(String))
            : (value != null && value !== "" ? String(value) : "");
        const byId = new Map(rewardOpts.map((r) => [String(r.id), r.name || String(r.id)]));
        return (
            <TextField size="small" fullWidth select label="Récompense"
                value={rawSel}
                onChange={(e) => { const v = e.target.value; onChange(multi ? (Array.isArray(v) ? v : [v].filter(Boolean)) : v); }}
                sx={sx}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                    multiple: multi,
                    displayEmpty: true,
                    renderValue: (s) => multi
                        ? (Array.isArray(s) && s.length ? s.map((id) => byId.get(String(id)) || id).join(", ") : <em style={{color:"#94a3b8"}}>Sélectionner…</em>)
                        : (byId.get(String(s)) || s || <em style={{color:"#94a3b8"}}>Sélectionner…</em>)
                }}>
                {rewardOpts.length === 0 && <MenuItem disabled value=""><em>Chargement…</em></MenuItem>}
                {rewardOpts.map((r) => <MenuItem key={r.id} value={String(r.id)}>{r.name || r.id}</MenuItem>)}
            </TextField>
        );
    }

    if (hasDynamicDate && isArrayOp) {
        const arrVal = Array.isArray(value) ? value : ["", ""];
        const lo = arrVal[0] ?? ""; const hi = arrVal[1] ?? "";
        const loIsSys = isSysObj(lo); const hiIsSys = isSysObj(hi);
        const updateLo = (v) => onChange([v, hi]);
        const updateHi = (v) => onChange([lo, v]);
        const sysInput = (val, onChg) => (
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ p: 1, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }}>
                <TextField select size="small" value={isSysObj(val) ? val.$system : "today"} onChange={(e) => onChg(makeSysObj(e.target.value, isSysObj(val) ? (val.add_days || 0) : 0, isDateOnly ? "mmdd" : (isSysObj(val) ? val.format : undefined)))} sx={{ ...sx, minWidth: 160 }}>
                    {SYS_PRESETS.map((p) => <MenuItem key={p.k} value={p.k}>{p.l}</MenuItem>)}
                </TextField>
                <Typography fontSize={13} color="text.secondary" fontWeight={700}>+</Typography>
                <TextField size="small" type="number" value={isSysObj(val) ? (val.add_days || 0) : 0} onChange={(e) => onChg(makeSysObj(isSysObj(val) ? val.$system : "today", Number(e.target.value), isDateOnly ? "mmdd" : (isSysObj(val) ? val.format : undefined)))} sx={{ ...sx, width: 80 }} />
                <Typography fontSize={12} color="text.secondary" fontWeight={500}>jours</Typography>
                {!isDateOnly && <TextField size="small" label="Format (optionnel)" placeholder="mmdd" value={isSysObj(val) ? (val.format || "") : ""} onChange={(e) => onChg(makeSysObj(isSysObj(val) ? val.$system : "today", isSysObj(val) ? (val.add_days || 0) : 0, e.target.value || undefined))} sx={{ ...sx, width: 120, ml: 1 }} />}
            </Box>
        );
        return (
            <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontSize={11} color="text.secondary">Mode valeur :</Typography>
                    {[{k:false,l:"Fixe"},{k:true,l:"🔁 Dynamique"}].map(({k,l}) => (
                        <Box key={String(k)} onClick={() => { setDynPair(k); onChange(k ? [makeSysObj("today",0,"mmdd"), makeSysObj("today",7,"mmdd")] : ["",""]); }}
                            sx={{ px:1.5, py:0.3, borderRadius:99, cursor:"pointer", fontSize:11, fontWeight:700, bgcolor: dynPair===k ? COLOR.main : "#f1f5f9", color: dynPair===k ? "#fff" : "#64748b", transition:"all .15s" }}>{l}</Box>
                    ))}
                </Box>
                {dynPair ? (
                    <Box display="flex" flexDirection="column" gap={1}>
                        <Typography fontSize={10} color="text.secondary" fontWeight={600}>Borne basse</Typography>
                        {sysInput(lo, updateLo)}
                        <Typography fontSize={10} color="text.secondary" fontWeight={600}>Borne haute</Typography>
                        {sysInput(hi, updateHi)}
                    </Box>
                ) : operator === "between" ? (
                    <Box display="flex" gap={1}>
                        <TextField size="small" fullWidth label={isDateOnly ? "De (MM-JJ)" : "De"} type={isDateOnly ? "text" : "datetime-local"} InputLabelProps={!isDateOnly ? { shrink: true } : undefined} placeholder={isDateOnly ? "01-01" : ""} value={Array.isArray(value) ? value[0] || "" : ""} onChange={(e) => onChange([e.target.value, Array.isArray(value) ? value[1] || "" : ""])} sx={sx} />
                        <TextField size="small" fullWidth label={isDateOnly ? "À (MM-JJ)" : "À"} type={isDateOnly ? "text" : "datetime-local"} InputLabelProps={!isDateOnly ? { shrink: true } : undefined} placeholder={isDateOnly ? "12-31" : ""} value={Array.isArray(value) ? value[1] || "" : ""} onChange={(e) => onChange([Array.isArray(value) ? value[0] || "" : "", e.target.value])} sx={sx} />
                    </Box>
                ) : (
                    <TextField size="small" fullWidth label="Valeurs (ex: 01-01, 01-31)" value={Array.isArray(value) && !loIsSys && !hiIsSys ? value.join(", ") : ""} onChange={(e) => { const p = e.target.value.split(",").map((s) => s.trim()); onChange(p); }} helperText="Format MM-JJ pour les dates" sx={sx} />
                )}
            </Box>
        );
    }

    if (isArrayOp) {
        if (operator === "between") {
            const arr = Array.isArray(value) ? value : ["", ""];
            return (
                <Box display="flex" gap={1}>
                    <TextField size="small" fullWidth type={isNumeric ? "number" : "text"} label="De" value={arr[0] ?? ""} onChange={(e) => { const v = e.target.value; onChange([isNumeric && v !== "" ? Number(v) : v, arr[1]]); }} sx={sx} />
                    <TextField size="small" fullWidth type={isNumeric ? "number" : "text"} label="À" value={arr[1] ?? ""} onChange={(e) => { const v = e.target.value; onChange([arr[0], isNumeric && v !== "" ? Number(v) : v]); }} sx={sx} />
                </Box>
            );
        }
        const disp = Array.isArray(value) ? value.join(", ") : (value ?? "");
        return <TextField size="small" fullWidth label="Valeurs (séparées par virgule)" value={disp} onChange={(e) => { const p = e.target.value.split(",").map((s) => { const t = s.trim(); const n = Number(t); return t !== "" && !isNaN(n) ? n : t; }); onChange(p); }} helperText="Ex: A, B" sx={sx} />;
    }
    if (isNumeric) return <TextField size="small" fullWidth type="number" label="Valeur" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} sx={sx} helperText={numericOps.has(operator) && !numericFields.includes(field) ? `« ${field} » traité comme numérique` : undefined} />;
    if (hasDynamicDate) {
        // Single date field with optional dynamic mode
        const sysVal = isSysObj(value) ? value : { $system: isDateOnly ? "today" : "now", add_days: 0 };
        return (
            <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontSize={11} color="text.secondary">Mode valeur :</Typography>
                    {[{k:false,l:"Fixe"},{k:true,l:"🔁 Dynamique"}].map(({k,l}) => (
                        <Box key={String(k)} onClick={() => { setUseDynamic(k); onChange(k ? makeSysObj(isDateOnly ? "today" : "now") : ""); }}
                            sx={{ px:1.5, py:0.3, borderRadius:99, cursor:"pointer", fontSize:11, fontWeight:700, bgcolor: useDynamic===k ? COLOR.main : "#f1f5f9", color: useDynamic===k ? "#fff" : "#64748b", transition:"all .15s" }}>{l}</Box>
                    ))}
                </Box>
                {useDynamic ? (
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ p: 1, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }}>
                        <TextField select size="small" value={sysVal.$system || (isDateOnly ? "today" : "now")} onChange={(e) => onChange(makeSysObj(e.target.value, sysVal.add_days || 0, isDateOnly ? "mmdd" : sysVal.format))} sx={{ ...sx, minWidth: 160 }}>
                            {SYS_PRESETS.map((p) => <MenuItem key={p.k} value={p.k}>{p.l}</MenuItem>)}
                        </TextField>
                        <Typography fontSize={13} color="text.secondary" fontWeight={700}>+</Typography>
                        <TextField size="small" type="number" value={sysVal.add_days || 0} onChange={(e) => onChange(makeSysObj(sysVal.$system, Number(e.target.value), isDateOnly ? "mmdd" : sysVal.format))} sx={{ ...sx, width: 80 }} />
                        <Typography fontSize={12} color="text.secondary" fontWeight={500}>jours</Typography>
                        {!isDateOnly && <TextField size="small" label="Format (optionnel)" placeholder="mmdd" value={sysVal.format || ""} onChange={(e) => onChange(makeSysObj(sysVal.$system, sysVal.add_days || 0, e.target.value || undefined))} sx={{ ...sx, width: 120, ml: 1 }} />}
                    </Box>
                ) : (
                    <TextField size="small" fullWidth label={isDateOnly ? "Date" : "Date / Heure"} type={isDateOnly ? "date" : "datetime-local"} InputLabelProps={{ shrink: true }} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} sx={sx} />
                )}
            </Box>
        );
    }
    return <TextField size="small" fullWidth label="Valeur" value={typeof value === "string" ? value : JSON.stringify(value ?? "")} onChange={(e) => onChange(e.target.value)} sx={sx} />;
}

// ─── Component Nodes ──────────────────────────────────────────────────────────

const NS_COLORS = { payload: "#15803d", customer: "#0369a1", system: "#7c3aed" };
const OP_LABELS = { eq:"= (égal)",neq:"≠ (différent)",gt:"> (supérieur)",gte:"≥ (sup. ou égal)",lt:"< (inférieur)",lte:"≤ (inf. ou égal)",between:"Entre [lo, hi]",in:"Dans la liste",contains:"Contient",exists:"Existe" };

function ConditionRow({ item, onUpdateItem, onRemoveItem, fieldOptions, operatorOptions, loadingFields, allowCreate, brandName, fieldCatalog }) {
    const ns = item.field ? item.field.split(".")[0] : null;
    const nsColor = NS_COLORS[ns] || "#e2e8f0";
    const sx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

    const updateField = (patch) => {
        onUpdateItem({ ...item, ...patch });
    };

    return (
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fff", border: "1px solid #e2e8f0", borderLeft: `4px solid ${nsColor}` }}>
            <Box display="flex" gap={1} alignItems="flex-start">
                <Box flex={1} display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <Box flex={2} minWidth={180}>
                            <Autocomplete freeSolo={allowCreate} forcePopupIcon size="small" options={fieldOptions}
                                slotProps={autocompletePopperSlotProps}
                                groupBy={(o) => o.startsWith("customer.") ? "Profil client" : o.startsWith("payload.") ? "Payload" : o.startsWith("system.") ? "Système" : "Autres"}
                                getOptionLabel={(o) => !o ? "" : o.includes(".") ? o.substring(o.indexOf(".") + 1) : o}
                                isOptionEqualToValue={(o, v) => o === v}
                                value={item.field || null}
                                loading={loadingFields}
                                onChange={(_, v) => updateField({ field: v || "" })}
                                onInputChange={(_, v, r) => { if (r === "input") updateField({ field: v }); }}
                                renderInput={(p) => (
                                    <TextField {...p} label="Champ" size="small" sx={sx} placeholder="payload.amount"
                                        InputProps={{ ...p.InputProps, endAdornment: <>{loadingFields && <CircularProgress size={14} />}{p.InputProps.endAdornment}</> }} />
                                )}
                                renderOption={(p, o) => {
                                    const k = o.split(".")[0];
                                    return <li {...p} key={o}><Typography fontSize={13} fontWeight={600} color={NS_COLORS[k] || "#334155"} py={0.25}>{o.includes(".") ? o.substring(o.indexOf(".") + 1) : o}</Typography></li>;
                                }}
                            />
                        </Box>
                        <Box flex={1} minWidth={130}>
                            <TextField select size="small" fullWidth label="Opérateur" value={item.operator || "eq"} onChange={(e) => updateField({ operator: e.target.value })} sx={sx}>
                                {operatorOptions.map((op) => <MenuItem key={op} value={op}><Typography fontSize={12}>{OP_LABELS[op] || op}</Typography></MenuItem>)}
                            </TextField>
                        </Box>
                    </Box>
                    {item.field && <ValueEditor field={item.field} operator={item.operator || "eq"} value={item.value} brandName={brandName} fieldCatalog={fieldCatalog} onChange={(v) => updateField({ value: v })} />}
                    {ns && <Typography fontSize={10} color={nsColor} fontWeight={600} px={0.5}>{ns === "payload" ? "📦 Payload" : ns === "customer" ? "👤 Profil client" : "⚙️ Système"}</Typography>}
                </Box>
                <IconButton size="small" onClick={() => onRemoveItem(item.id)} sx={{ color: "#dc2626", mt: 0.5, bgcolor: "#fee2e2", "&:hover": { bgcolor: "#fecaca" } }}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Box>
        </Box>
    );
}

function GroupBox({ item, onUpdateItem, onRemoveItem, level, fieldOptions, operatorOptions, loadingFields, allowCreate, brandName, fieldCatalog }) {
    const conds = item.conditions || [];
    const groupType = item.groupType || "and";
    const setGroupType = (gt) => onUpdateItem({ ...item, groupType: gt });

    const GT_OPTIONS = [
        { k: "and", l: "ET", tip: "Toutes les conditions doivent être vraies", bg: COLOR.main },
        { k: "or", l: "OU", tip: "Au moins une condition doit être vraie", bg: "#f59e0b" },
        { k: "not", l: "NON", tip: "Inverse le résultat (Aucune condition ne doit être vraie)", bg: "#dc2626" },
    ];
    const gtMeta = GT_OPTIONS.find((o) => o.k === groupType) || GT_OPTIONS[0];

    const addChildItem = (type) => {
        const newId = (type === "group" ? "g_" : "c_") + Date.now() + "_" + Math.random().toString(36).substr(2,9);
        const newItem = type === "group" 
            ? { id: newId, type: "group", groupType: "and", conditions: [{ id: "c_"+Date.now(), type: "condition", field: "", operator: "eq", value: "" }] }
            : { id: newId, type: "condition", field: "", operator: "eq", value: "" };
        
        onUpdateItem({ ...item, conditions: [...conds, newItem] });
    };

    const handleUpdateChild = (patchedChild) => {
        onUpdateItem({ ...item, conditions: conds.map(c => c.id === patchedChild.id ? patchedChild : c) });
    };

    const handleRemoveChild = (id) => {
        onUpdateItem({ ...item, conditions: conds.filter(c => c.id !== id) });
    };

    const shared = { fieldOptions, operatorOptions, loadingFields, allowCreate, brandName, fieldCatalog };
    
    // ── Depth-based background palette ─────────────────────────────────────────
    // Each depth gets a distinct tint so nested groups are instantly recognisable.
    const depthBg = [
        "#eef9ff", // level 0 – light sky
        "#fefce8", // level 1 – light amber
        "#f0fdf4", // level 2 – light mint
        "#fdf4ff", // level 3 – light violet
        "#fff1f2", // level 4 – light rose
    ];
    const bgColor = depthBg[Math.min(level, depthBg.length - 1)];

    // Operator accent color drives the left border and icon tint
    const accentColor = gtMeta.bg;

    return (
        <Box sx={{
            borderRadius: 2,
            border: `1px solid ${accentColor}30`,
            borderLeft: `4px solid ${accentColor}`,
            bgcolor: bgColor,
            p: 2,
            position: "relative",
            boxShadow: `0 1px 4px ${accentColor}18`,
        }}>
            {/* Depth label */}
            {level > 0 && (
                <Typography sx={{
                    position: "absolute", top: 6, right: 10,
                    fontSize: 9, fontWeight: 700, color: `${accentColor}80`,
                    textTransform: "uppercase", letterSpacing: 1,
                }}>
                    Niveau {level + 1}
                </Typography>
            )}

            {/* Group Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <FolderOpenIcon sx={{ fontSize: 18, color: accentColor }} />
                    <Box sx={{ display: "flex", borderRadius: 99, overflow: "hidden", border: `1.5px solid ${accentColor}50`, bgcolor: "#fff", boxShadow: `0 1px 3px ${accentColor}20` }}>
                        {GT_OPTIONS.map(({ k, l, bg }) => (
                            <Tooltip key={k} title={GT_OPTIONS.find((o) => o.k === k)?.tip || ""}>
                                <Box onClick={() => setGroupType(k)} sx={{
                                    px: 2, py: 0.5, cursor: "pointer", fontSize: 11, fontWeight: 800,
                                    bgcolor: groupType === k ? bg : "transparent",
                                    color: groupType === k ? "#fff" : "#64748b",
                                    transition: "all .2s", userSelect: "none",
                                    "&:hover": { bgcolor: groupType === k ? bg : "#f8fafc", color: groupType === k ? "#fff" : "#334155" },
                                }}>{l}</Box>
                            </Tooltip>
                        ))}
                    </Box>
                    {groupType === "not" && <Typography fontSize={11} color="#dc2626" fontWeight={600} ml={1}>⚠️ Inversion</Typography>}
                </Box>

                <Box display="flex" gap={1} alignItems="center">
                     {(groupType !== "not" || conds.length === 0) && (
                        <>
                             <Button size="small" variant="outlined" onClick={() => addChildItem("condition")}
                                sx={{ fontSize: 11, textTransform: "none", color: COLOR.main, borderColor: `${COLOR.main}50`, py: 0.2, px: 1, minWidth: 0, bgcolor: "#fff" }}>
                                + Condition
                             </Button>
                             <Button size="small" variant="outlined" onClick={() => addChildItem("group")}
                                sx={{ fontSize: 11, textTransform: "none", color: "#7c3aed", borderColor: "#7c3aed50", py: 0.2, px: 1, minWidth: 0, bgcolor: "#fff" }}>
                                + Sous-groupe
                             </Button>
                        </>
                     )}
                     <IconButton size="small" onClick={() => onRemoveItem(item.id)} sx={{ color: "#dc2626", ml: 1, bgcolor: "#fee2e2", "&:hover": { bgcolor: "#fecaca" } }}>
                         <DeleteOutlineIcon fontSize="small" />
                     </IconButton>
                </Box>
            </Box>

            {/* Group Content */}
            <Box sx={{ pl: 1, borderLeft: `2px solid ${accentColor}20` }}>
                {conds.length === 0 ? (
                    <Typography fontSize={12} color="text.secondary" textAlign="center" py={2} fontStyle="italic">
                        Ce groupe est vide. Ajoutez des conditions ou sous-groupes.
                    </Typography>
                ) : (
                    conds.map((child, idx) => (
                        <ConditionItem
                            key={child.id}
                            item={child}
                            isFirst={idx === 0}
                            parentGroupType={groupType}
                            onUpdateItem={handleUpdateChild}
                            onRemoveItem={handleRemoveChild}
                            level={level + 1}
                            {...shared}
                        />
                    ))
                )}
            </Box>
        </Box>
    );
}

const ConditionItem = ({ item, isFirst, onUpdateItem, onRemoveItem, level = 0, ...shared }) => {
    return (
        <React.Fragment>
            {!isFirst && (
                <Box sx={{ height: "1px", bgcolor: "#e2e8f0", my: 1.5 }} />
            )}
            
            {item.type === "group" ? (
                <GroupBox item={item} onUpdateItem={onUpdateItem} onRemoveItem={onRemoveItem} level={level} {...shared} />
            ) : (
                <ConditionRow item={item} onUpdateItem={onUpdateItem} onRemoveItem={onRemoveItem} level={level} {...shared} />
            )}
        </React.Fragment>
    );
};

// ─── Main ConditionBuilder ───────────────────────────────────────────────────

export default function ConditionBuilder({
    brandName, transactionType, conditionCatalog, uiHints,
    combinator, rows, onChange,
    payloadPaths, hasPayloadSchema,
}) {
    const [fieldOpts, setFieldOpts] = useState([]);
    const [fieldCatalog, setFieldCatalog] = useState(null);
    const [loadingFields, setLoadingFields] = useState(false);

    const fieldDatasource = uiHints?.field?.datasource || {
        endpoint: "/admin/rules/ui-options/condition-fields",
        brandVia: "X-Brand",
        valueField: "items",
        dependsOn: { transaction_type: "transaction_type" },
    };
    const allowCreate = uiHints?.field?.allowCreate !== false;
    const operatorOptions = uiHints?.operator?.options || ["eq","neq","gt","gte","lt","lte","between","in","contains","exists"];

    useEffect(() => {
        if (!brandName || !transactionType) { setFieldOpts([]); setFieldCatalog(null); return; }
        const url = new URL(`${LOYALTY_API_BASE}${fieldDatasource.endpoint}`);
        if (fieldDatasource.dependsOn?.transaction_type) url.searchParams.set("transaction_type", transactionType);
        const headers = fieldDatasource.brandVia ? { [fieldDatasource.brandVia]: brandName } : {};
        setLoadingFields(true);
        loyaltyFetch(url.toString(), { headers })
            .then((r) => r.ok ? r.json() : null)
            .then((res) => {
                const items = res?.[fieldDatasource.valueField || "items"];
                setFieldOpts(Array.isArray(items) ? items : []);
                setFieldCatalog(res && typeof res === "object" ? res : null);
            })
            .catch(() => { setFieldOpts([]); setFieldCatalog(null); })
            .finally(() => setLoadingFields(false));
    }, [brandName, transactionType]);

    const sortedFieldOpts = React.useMemo(() =>
        [...fieldOpts].sort((a, b) => {
            const g = (o) => o.startsWith("customer.") ? 1 : o.startsWith("payload.") ? 2 : o.startsWith("system.") ? 3 : 4;
            return g(a) !== g(b) ? g(a) - g(b) : a.localeCompare(b);
        }), [fieldOpts]);

    const [topCombinator, setTopCombinator] = useState(combinator || "and");
    useEffect(() => { setTopCombinator(combinator || "and"); }, [combinator]);

    const emit = (newRows, tc) => {
        onChange(tc ?? topCombinator, newRows);
    };

    const addRootItem = (type) => {
         const newId = (type === "group" ? "g_" : "c_") + Date.now() + "_" + Math.random().toString(36).substr(2,9);
         const newItem = type === "group" 
            ? { id: newId, type: "group", groupType: "and", conditions: [{ id: "c_"+Date.now(), type: "condition", field: "", operator: "eq", value: "" }] }
            : { id: newId, type: "condition", field: "", operator: "eq", value: "" };
         emit([...rows, newItem]);
    };
    
    const handleUpdateRootItem = (patchedItem) => {
         emit(rows.map(r => r.id === patchedItem.id ? patchedItem : r));
    }
    
    const handleRemoveRootItem = (id) => {
         emit(rows.filter(r => r.id !== id));
    }

    const changeTopCombinator = (tc) => {
        setTopCombinator(tc);
        emit(rows, tc);
    };

    const shared = { fieldOptions: sortedFieldOpts, operatorOptions, loadingFields, allowCreate, brandName, fieldCatalog };

    return (
        <Box sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${COLOR.main}40`, bgcolor: "#f8fafc" }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
                 <Box display="flex" alignItems="center" gap={1}>
                     <FilterAltIcon sx={{ fontSize: 22, color: COLOR.main }} />
                     <Typography fontSize={15} fontWeight={800} color="#0f172a">Conditions d'application</Typography>
                     {loadingFields && <CircularProgress size={14} sx={{ color: COLOR.main, ml: 1 }} />}
                 </Box>
                 
                 <Box display="flex" alignItems="center" gap={2}>
                     {/* Global combinator — shown only when there are ≥2 root items */}
                     {rows.length > 1 && (
                         <Box display="flex" alignItems="center" gap={1}>
                             <Typography fontSize={12} color="text.secondary" fontWeight={600}>Lier par :</Typography>
                             <Box sx={{ display: "flex", borderRadius: 99, overflow: "hidden", border: "1px solid #cbd5e1", bgcolor: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
                                 {[{ k: "and", l: "ET", bg: COLOR.main }, { k: "or", l: "OU", bg: "#f59e0b" }].map(({ k, l, bg }) => (
                                     <Box key={k} onClick={() => changeTopCombinator(k)} sx={{
                                         px: 2.5, py: 0.5, cursor: "pointer", fontSize: 12, fontWeight: 800,
                                         bgcolor: topCombinator === k ? bg : "transparent",
                                         color: topCombinator === k ? "#fff" : "#64748b",
                                         transition: "all .2s", userSelect: "none",
                                         "&:hover": { bgcolor: topCombinator === k ? bg : "#f8fafc", color: topCombinator === k ? "#fff" : "#334155" },
                                     }}>{l}</Box>
                                 ))}
                             </Box>
                         </Box>
                     )}
                     <Box display="flex" gap={1}>
                     <Button size="small" variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => addRootItem("condition")}
                         disabled={!transactionType}
                         sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, fontSize: 13, px: 2, bgcolor: COLOR.main, color: "#fff", "&:hover": { bgcolor: "#0284c7" } }}>
                         Condition
                     </Button>
                     <Button size="small" variant="outlined" startIcon={<FolderOpenIcon />} onClick={() => addRootItem("group")}
                         disabled={!transactionType}
                         sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, fontSize: 13, px: 2, color: "#7c3aed", borderColor: "#7c3aed", bgcolor: "#fff", "&:hover": { bgcolor: "#f3e8ff", borderColor: "#7c3aed" } }}>
                         Groupe
                     </Button>
                     </Box>
                 </Box>
            </Box>
            
            {/* Content */}
            <Box sx={{ minHeight: rows.length === 0 ? 100 : "auto", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                {!transactionType && <Typography fontSize={14} color="text.secondary" textAlign="center" py={3}>Sélectionnez un type de transaction pour configurer les conditions.</Typography>}
                {transactionType && rows.length === 0 && <Typography fontSize={14} color="text.secondary" textAlign="center" py={3} fontStyle="italic">Aucune condition — la règle s'appliquera à toutes les transactions.</Typography>}
                
                {rows.map((item, idx) => (
                     <ConditionItem 
                         key={item.id} 
                         item={item} 
                         isFirst={idx === 0} 
                         parentGroupType="and"
                         onUpdateItem={handleUpdateRootItem} 
                         onRemoveItem={handleRemoveRootItem}
                         level={0}
                         {...shared}
                     />
                ))}
            </Box>
        </Box>
    );
}
